import { AxiosInstance, AxiosResponse } from 'axios';
import {
  CreateDebtRequest,
  AccountMovement,
  AccountBalanceSummary,
  Debt,
  DebtSearchParams,
  PaginatedResponse,
  SearchParams,
  UpdateDebtRequest,
} from '../../types';
import { isApiErrorStatus } from './http';

/** Debt and customer-balance API calls. */
export class DebtApi {
  constructor(private readonly http: AxiosInstance) {}

  async getDebts(): Promise<Debt[]> {
    return this.getAllPages('/debts');
  }

  async getDebtsPaginated(params: SearchParams): Promise<PaginatedResponse<Debt>> {
    const response: AxiosResponse<PaginatedResponse<Debt>> = await this.http.get('/debts', { params });
    return response.data;
  }

  async getDebtsFilteredPaginated(params: DebtSearchParams): Promise<PaginatedResponse<Debt>> {
    const response: AxiosResponse<PaginatedResponse<Debt>> = await this.http.get('/debts/search', { params });
    return response.data;
  }

  async getAccountMovements(params: DebtSearchParams, signal?: AbortSignal): Promise<PaginatedResponse<AccountMovement>> {
    const requestConfig = signal ? { params, signal } : { params };
    const response: AxiosResponse<PaginatedResponse<AccountMovement>> = await this.http.get('/debts/account-movements', requestConfig);
    return response.data;
  }

  async getAccountBalanceSummary(companyIdOrSignal?: string | AbortSignal, signal?: AbortSignal): Promise<AccountBalanceSummary> {
    const companyId = typeof companyIdOrSignal === 'string' ? companyIdOrSignal : undefined;
    const requestSignal = typeof companyIdOrSignal === 'string' ? signal : companyIdOrSignal;
    const params = companyId ? { companyId } : undefined;
    try {
      const response: AxiosResponse<AccountBalanceSummary> = requestSignal
        ? await this.http.get('/debts/account-movements/balances', { ...(params ? { params } : {}), signal: requestSignal })
        : params
          ? await this.http.get('/debts/account-movements/balances', { params })
          : await this.http.get('/debts/account-movements/balances');
      return response.data;
    } catch (error: unknown) {
      if (isApiErrorStatus(error, 404)) {
        throw new Error('Balance summary is unavailable on this backend version. Deploy the dashboard API before using reports.');
      }
      throw error;
    }
  }
  async getAccountMovementsByCustomer(customerId: string): Promise<AccountMovement[]> {
    const pageSize = 100;
    const first = await this.getAccountMovements({ customerId, page: 0, size: pageSize });
    if (first.totalPages <= 1) return first.content;

    const remaining = await Promise.all(
      Array.from({ length: first.totalPages - 1 }, (_, index) =>
        this.getAccountMovements({ customerId, page: index + 1, size: pageSize }),
      ),
    );
    return [first, ...remaining].flatMap(page => page.content);
  }

  async getDebt(id: string): Promise<Debt> {
    const response: AxiosResponse<Debt> = await this.http.get(`/debts/${id}`);
    return response.data;
  }

  async getDebtsByCustomer(customerId: string): Promise<Debt[]> {
    return this.getAllPages(`/debts/customer/${customerId}`);
  }

  async getCustomerTotalDebt(customerId: string): Promise<{ totalDebt: number }> {
    const response: AxiosResponse<{ totalDebt: number }> = await this.http.get(`/debts/customer/${customerId}/total`);
    return response.data;
  }

  async createDebt(debt: CreateDebtRequest): Promise<Debt> {
    const response: AxiosResponse<Debt> = await this.http.post('/debts', debt);
    return response.data;
  }

  async updateDebt(id: string, debt: UpdateDebtRequest): Promise<Debt> {
    const response: AxiosResponse<Debt> = await this.http.put(`/debts/${id}`, debt);
    return response.data;
  }

  async deleteDebt(id: string): Promise<void> {
    await this.http.delete(`/debts/${id}`);
  }

  /**
   * The backend caps page size at 100. Keep the legacy array-based API for
   * screens that paginate in memory, but walk all server pages safely.
   */
  private async getAllPages(path: string): Promise<Debt[]> {
    const pageSize = 100;
    const first: AxiosResponse<PaginatedResponse<Debt>> = await this.http.get(path, {
      params: { page: 0, size: pageSize },
    });
    const totalPages = first.data.totalPages > 1 ? first.data.totalPages : 1;

    if (totalPages === 1) {
      return first.data.content;
    }

    const remaining = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, index) =>
        this.http.get<PaginatedResponse<Debt>>(path, {
          params: { page: index + 1, size: pageSize },
        }),
      ),
    );

    return [first, ...remaining].flatMap(response => response.data.content);
  }
}
